/**
 * `VoiceConfirmationCard` — displayed inside the voice preview sheet AFTER
 * Whisper + Claude finish parsing, but BEFORE we navigate to /review.
 *
 * Purpose: give the tradie a one-glance sanity check on what was understood.
 * They can:
 *   - Tap "Looks right" → navigate to /review with the parsed shape
 *   - Tap "Edit" → still navigates to /review (identical flow) — this is
 *     the "I want to tweak a field" affordance
 *   - Tap "Re-record" → discard and start over
 *
 * WHY a card and not just a toast: the tradie is often on-site, gloves off,
 * mid-conversation. A silent auto-nav feels magic in demos and terrifying
 * in the field. The card is the fingerprint on the receipt.
 */

import type { VoiceParseResult } from "@/agent/voice";
import { formatMoney, formatDate } from "@/shared/format";
import { CheckCircle2, Pencil, RotateCcw, Sparkles } from "lucide-react";

interface Props {
  parsed: VoiceParseResult;
  onConfirm: () => void;
  onEdit: () => void;
  onRerecord: () => void;
  busy?: boolean;
}

/** Small helper — supplier can come back as an empty string when Claude
 * couldn't pin it down. Show a friendly placeholder instead of a blank. */
const supplierOr = (s: string) => (s.trim().length > 0 ? s : "Unknown supplier");

export function VoiceConfirmationCard({
  parsed,
  onConfirm,
  onEdit,
  onRerecord,
  busy = false,
}: Props) {
  const total = typeof parsed.total === "number" ? parsed.total : 0;
  const gst = typeof parsed.gst === "number" ? parsed.gst : 0;
  const overallConfidence =
    typeof parsed.confidence?.overall === "number"
      ? parsed.confidence.overall
      : null;

  // Conservative UI: if Claude flags the parse as low-confidence we surface
  // it inline so the tradie doesn't blindly hit Confirm.
  const lowConfidence =
    overallConfidence !== null && overallConfidence < 55;

  return (
    <div
      data-testid="voice-confirm-card"
      className="bi-surface p-4 space-y-3 border border-[#FF9500]/25"
    >
      <div className="flex items-start gap-2">
        <div className="w-8 h-8 rounded-lg bg-[#FF9500]/20 text-[#FF9500] flex items-center justify-center shrink-0">
          <Sparkles className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-white/50 font-heading">
            Heard by BuildIntel
          </div>
          <div
            data-testid="voice-confirm-line"
            className="mt-0.5 font-heading font-bold text-base text-white leading-snug"
          >
            {parsed.confirmation_line ||
              `Heard ${formatMoney(total)} at ${supplierOr(parsed.supplier)}.`}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 text-center">
        <div
          data-testid="voice-confirm-total"
          className="rounded-lg bg-white/5 py-2"
        >
          <div className="text-[10px] uppercase tracking-wider text-white/40">
            Total
          </div>
          <div className="font-heading font-bold text-base text-white tabular-nums">
            {formatMoney(total)}
          </div>
        </div>
        <div
          data-testid="voice-confirm-gst"
          className="rounded-lg bg-white/5 py-2"
        >
          <div className="text-[10px] uppercase tracking-wider text-white/40">
            GST
          </div>
          <div className="font-heading font-bold text-base text-white tabular-nums">
            {formatMoney(gst)}
          </div>
        </div>
        <div
          data-testid="voice-confirm-date"
          className="rounded-lg bg-white/5 py-2"
        >
          <div className="text-[10px] uppercase tracking-wider text-white/40">
            Date
          </div>
          <div className="font-heading font-bold text-sm text-white">
            {parsed.entry_date ? formatDate(parsed.entry_date) : "—"}
          </div>
        </div>
      </div>

      <div
        data-testid="voice-confirm-supplier-row"
        className="flex items-center justify-between text-sm"
      >
        <span className="text-white/50">Supplier</span>
        <span className="text-white font-medium truncate ml-2">
          {supplierOr(parsed.supplier)}
        </span>
      </div>

      {lowConfidence && (
        <div
          data-testid="voice-confirm-low-conf"
          className="text-[11px] text-[#FBBF24] bg-[#FBBF24]/10 rounded-lg px-2 py-1.5"
        >
          Not fully confident — double-check the fields on the next screen.
        </div>
      )}

      <div className="grid grid-cols-3 gap-2 pt-1">
        <button
          type="button"
          data-testid="voice-confirm-rerecord"
          onClick={onRerecord}
          disabled={busy}
          className="h-11 rounded-xl bi-ghost-btn flex items-center justify-center gap-1.5 disabled:opacity-50 text-sm"
        >
          <RotateCcw className="w-3.5 h-3.5" /> Re-record
        </button>
        <button
          type="button"
          data-testid="voice-confirm-edit"
          onClick={onEdit}
          disabled={busy}
          className="h-11 rounded-xl bi-ghost-btn flex items-center justify-center gap-1.5 disabled:opacity-50 text-sm"
        >
          <Pencil className="w-3.5 h-3.5" /> Edit
        </button>
        <button
          type="button"
          data-testid="voice-confirm-ok"
          onClick={onConfirm}
          disabled={busy}
          className="h-11 rounded-xl bi-accent-btn flex items-center justify-center gap-1.5 disabled:opacity-50 text-sm shadow-[0_10px_30px_-15px_rgba(255,149,0,0.6)]"
        >
          <CheckCircle2 className="w-3.5 h-3.5" /> Looks right
        </button>
      </div>
    </div>
  );
}

export default VoiceConfirmationCard;
